import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ViewerToolbar = ({ catalog, currentPage, totalPages, onPageJump, onShare, shareCopied, onDownload, onEmbed, onMenu, onSearch, embedMode = false, companyLogoUrl }) => {
  const { t } = useTranslation();
  const [isPageInputOpen, setIsPageInputOpen] = useState(false);
  const [pageInput, setPageInput] = useState('');
  const [isShareMenuOpen, setIsShareMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileShareExpanded, setIsMobileShareExpanded] = useState(false);

  const handlePageJump = () => {
    const pageNum = parseInt(pageInput, 10);
    if (pageNum >= 1 && pageNum <= totalPages) {
      onPageJump(pageNum);
      setIsPageInputOpen(false);
      setPageInput('');
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsMobileShareExpanded(false);
  };

  const handleShare = (platform) => {
    const url = `https://catalogflip.com/view/${catalog?.id}`;
    const text = `Check out ${catalog?.title} on CatalogFlip`;

    const shareUrls = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      email: `mailto:?subject=${encodeURIComponent(catalog?.title)}&body=${encodeURIComponent(text + ' ' + url)}`
    };

    if (platform === 'copy') {
      if (onShare) {
        onShare();
      } else {
        navigator.clipboard?.writeText(url);
      }
      setIsShareMenuOpen(false);
      closeMobileMenu();
    } else {
      window.open(shareUrls?.[platform], '_blank', 'width=600,height=400');
      setIsShareMenuOpen(false);
      closeMobileMenu();
    }
  };

  // Left section: company logo (embed desktop) or catalog info (non-embed)
  const renderLeftSection = () => {
    if (embedMode) {
      if (!companyLogoUrl) return null;
      return (
        <div className="hidden md:flex items-center">
          <img
            src={companyLogoUrl}
            alt="Company logo"
            className="h-8 max-w-[120px] object-contain"
          />
        </div>
      );
    }
    return (
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0">
          <Icon name="BookOpen" size={20} className="text-accent" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold text-foreground line-clamp-1">
            {catalog?.title}
          </h3>
          <p className="text-xs text-muted-foreground">
            {catalog?.company} • {catalog?.category}
          </p>
        </div>
      </div>
    );
  };

  const renderNavigation = () => {
    if (embedMode) {
      const activePage = currentPage === 0 ? 1 : currentPage;
      const isFirst = activePage <= 1;
      const isLast = activePage >= totalPages || totalPages - activePage === 1;
      const prevPage = activePage <= 2 ? 1 : activePage - 2;
      const nextPage = activePage === 1 ? 2 : Math.min(totalPages, activePage + 2);
      return (
        <>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onPageJump(1)}
            disabled={isFirst}
            className="w-8 h-8"
            title={t('catalogViewer.toolbar.firstPage')}
          >
            <Icon name="ChevronsLeft" size={16} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onPageJump(prevPage)}
            disabled={isFirst}
            className="w-8 h-8"
            title={t('catalogViewer.toolbar.previousPage')}
          >
            <Icon name="ChevronLeft" size={16} />
          </Button>
          {isPageInputOpen ? (
            <div className="flex items-center gap-1">
              <Input
                type="number"
                value={pageInput}
                onChange={(e) => setPageInput(e?.target?.value)}
                placeholder={`1-${totalPages}`}
                className="w-16"
                min={1}
                max={totalPages}
                onKeyPress={(e) => e?.key === 'Enter' && handlePageJump()}
              />
              <Button
                variant="secondary"
                size="sm"
                onClick={handlePageJump}
                iconName="Check"
                iconSize={14}
              >
                {t('catalogViewer.toolbar.go')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setIsPageInputOpen(false); setPageInput(''); }}
                iconName="X"
                iconSize={14}
              />
            </div>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsPageInputOpen(true)}
              iconName="Hash"
              iconPosition="left"
              iconSize={14}
            >
              {activePage} / {totalPages}
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onPageJump(nextPage)}
            disabled={isLast}
            className="w-8 h-8"
            title={t('catalogViewer.toolbar.nextPage')}
          >
            <Icon name="ChevronRight" size={16} />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onPageJump(totalPages)}
            disabled={isLast}
            className="w-8 h-8"
            title={t('catalogViewer.toolbar.lastPage')}
          >
            <Icon name="ChevronsRight" size={16} />
          </Button>
        </>
      );
    }

    // Non-embed navigation
    return isPageInputOpen ? (
      <div className="flex items-center gap-2">
        <Input
          type="number"
          value={pageInput}
          onChange={(e) => setPageInput(e?.target?.value)}
          placeholder={`1-${totalPages}`}
          className="w-20"
          min={1}
          max={totalPages}
          onKeyPress={(e) => e?.key === 'Enter' && handlePageJump()}
        />
        <Button
          variant="secondary"
          size="sm"
          onClick={handlePageJump}
          iconName="Check"
          iconSize={16}
        >
          {t('catalogViewer.toolbar.go')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setIsPageInputOpen(false);
            setPageInput('');
          }}
          iconName="X"
          iconSize={16}
        />
      </div>
    ) : (
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setIsPageInputOpen(true)}
        iconName="Hash"
        iconPosition="left"
        iconSize={16}
      >
        {t('catalogViewer.toolbar.page')} {currentPage} / {totalPages}
      </Button>
    );
  };

  // Desktop share dropdown (unchanged)
  const renderShareMenu = (isEmbed) => (
    <>
      <div className="fixed inset-0 z-40" onClick={() => setIsShareMenuOpen(false)} />
      <div className="absolute right-0 top-full mt-2 w-48 bg-popover border border-border rounded-lg shadow-elevation-lg z-50 overflow-hidden">
        {isEmbed && (
          <button onClick={() => handleShare('whatsapp')} className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-2">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
              <path d="M12 0C5.373 0 0 5.373 0 12c0 2.125.558 4.122 1.532 5.856L0 24l6.335-1.54A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.848 0-3.568-.5-5.051-1.37l-.361-.214-3.762.916.948-3.658-.235-.375A9.953 9.953 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
            </svg>
            WhatsApp
          </button>
        )}
        <button onClick={() => handleShare('twitter')} className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="#000000">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
          </svg>
          X (Twitter)
        </button>
        <button onClick={() => handleShare('facebook')} className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="#1877F2">
            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
          </svg>
          Facebook
        </button>
        <button onClick={() => handleShare('linkedin')} className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="#0A66C2">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
          </svg>
          LinkedIn
        </button>
        <button onClick={() => handleShare('email')} className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-2">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#EA4335" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect width="20" height="16" x="2" y="4" rx="2" />
            <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
          </svg>
          Email
        </button>
        <div className="border-t border-border" />
        <button onClick={() => handleShare('copy')} className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-2">
          <Icon name="Copy" size={16} />
          {t('catalogViewer.embedModal.copy')}
        </button>
      </div>
    </>
  );

  // Mobile hamburger panel — share, download, search, menu grouped here
  const renderMobileMenu = () => {
    const hasActions = onShare || onDownload || onSearch || onMenu;
    if (!hasActions) return null;

    return (
      <>
        {/* Backdrop */}
        <div className="fixed inset-0 z-40" onClick={closeMobileMenu} />

        {/* Panel — appears below toolbar */}
        <div className="absolute left-0 right-0 top-full z-50 bg-popover border-b border-border shadow-elevation-lg">

          {/* Search */}
          {onSearch && (
            <button
              onClick={() => { onSearch(); closeMobileMenu(); }}
              className="w-full px-4 py-3 text-left text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-3"
            >
              <Icon name="Search" size={18} className="text-muted-foreground" />
              <span>{t('catalogViewer.toolbar.search')}</span>
            </button>
          )}

          {/* Menu navigation */}
          {onMenu && (
            <button
              onClick={() => { onMenu(); closeMobileMenu(); }}
              className="w-full px-4 py-3 text-left text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-3"
            >
              <Icon name="LayoutList" size={18} className="text-muted-foreground" />
              <span>{t('catalogViewer.toolbar.menu')}</span>
            </button>
          )}

          {/* Share — accordion */}
          {onShare && (
            <>
              <button
                onClick={() => setIsMobileShareExpanded(prev => !prev)}
                className="w-full px-4 py-3 text-left text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-3"
              >
                <Icon name="Share2" size={18} className="text-muted-foreground" />
                <span className="flex-1">{t('catalogViewer.toolbar.share')}</span>
                <Icon name={isMobileShareExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-muted-foreground" />
              </button>

              {isMobileShareExpanded && (
                <div className="bg-muted/40 border-t border-border">
                  {/* WhatsApp */}
                  <button onClick={() => handleShare('whatsapp')} className="w-full px-8 py-2.5 text-left text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-3">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
                      <path d="M12 0C5.373 0 0 5.373 0 12c0 2.125.558 4.122 1.532 5.856L0 24l6.335-1.54A11.945 11.945 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 22c-1.848 0-3.568-.5-5.051-1.37l-.361-.214-3.762.916.948-3.658-.235-.375A9.953 9.953 0 012 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10z" />
                    </svg>
                    WhatsApp
                  </button>
                  <button onClick={() => handleShare('twitter')} className="w-full px-8 py-2.5 text-left text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-3">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                    X (Twitter)
                  </button>
                  <button onClick={() => handleShare('facebook')} className="w-full px-8 py-2.5 text-left text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-3">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#1877F2">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                    </svg>
                    Facebook
                  </button>
                  <button onClick={() => handleShare('linkedin')} className="w-full px-8 py-2.5 text-left text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-3">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#0A66C2">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                    LinkedIn
                  </button>
                  <button onClick={() => handleShare('email')} className="w-full px-8 py-2.5 text-left text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-3">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#EA4335" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect width="20" height="16" x="2" y="4" rx="2" />
                      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
                    </svg>
                    Email
                  </button>
                  <button onClick={() => handleShare('copy')} className="w-full px-8 py-2.5 text-left text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-3">
                    <Icon name="Copy" size={16} className="text-muted-foreground" />
                    {t('catalogViewer.embedModal.copy')}
                  </button>
                </div>
              )}
            </>
          )}

          {/* Download */}
          {onDownload && (
            <button
              onClick={() => { onDownload(); closeMobileMenu(); }}
              className="w-full px-4 py-3 text-left text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-3"
            >
              <Icon name="Download" size={18} className="text-muted-foreground" />
              <span>{t('catalogViewer.toolbar.download')}</span>
            </button>
          )}
        </div>
      </>
    );
  };

  // Whether to show the mobile hamburger button at all
  const hasMobileActions = !!(onShare || onDownload || onSearch || onMenu);

  return (
    <div className="bg-card border-b border-border relative">
      <div className="max-w-7xl mx-auto px-3 md:px-6 lg:px-8 py-2.5">
        {embedMode ? (
          <>
            {/* ── Mobile layout (< md) ── */}
            <div className="relative flex items-center justify-center md:hidden">
              {/* Logo — absolutely pinned to the left */}
              {companyLogoUrl && (
                <div className="absolute left-0 flex items-center">
                  <img
                    src={companyLogoUrl}
                    alt="Company logo"
                    className="h-7 max-w-[90px] object-contain"
                  />
                </div>
              )}

              {/* Navigation — centered */}
              <div className="flex items-center gap-0.5">
                {renderNavigation()}
              </div>

              {/* Hamburger — absolutely pinned to the right */}
              {hasMobileActions && (
                <button
                  onClick={() => setIsMobileMenuOpen(prev => !prev)}
                  className="absolute right-0 w-9 h-9 flex items-center justify-center rounded-md text-foreground hover:bg-muted transition-colors"
                  title="Más opciones"
                >
                  <Icon name={isMobileMenuOpen ? 'X' : 'Menu'} size={20} />
                </button>
              )}

              {/* Mobile dropdown panel */}
              {isMobileMenuOpen && renderMobileMenu()}
            </div>

            {/* ── Desktop layout (≥ md): 3-column grid, nav centered ── */}
            <div className="hidden md:grid md:grid-cols-3 items-center gap-2">
              {/* Col 1: company logo */}
              <div className="flex items-center">
                {renderLeftSection()}
              </div>

              {/* Col 2: navigation centered */}
              <div className="flex items-center justify-center gap-1">
                {renderNavigation()}
              </div>

              {/* Col 3: actions right-aligned */}
              <div className="flex items-center justify-end gap-1">
                {onSearch && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onSearch}
                    iconName="Search"
                    iconSize={16}
                    title={t('catalogViewer.toolbar.search')}
                  />
                )}
                {onMenu && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onMenu}
                    iconName="Menu"
                    iconSize={16}
                    title={t('catalogViewer.toolbar.menu')}
                  />
                )}
                {onShare && (
                  <div className="relative">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsShareMenuOpen(!isShareMenuOpen)}
                      iconName="Share2"
                      iconSize={16}
                      title={t('catalogViewer.toolbar.share')}
                    />
                    {isShareMenuOpen && renderShareMenu(true)}
                  </div>
                )}
                {onDownload && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onDownload}
                    iconName="Download"
                    iconSize={16}
                    title={t('catalogViewer.toolbar.download')}
                  />
                )}
              </div>
            </div>
          </>
        ) : (
          /* Non-embed mode: original flex justify-between layout */
          <div className="flex items-center justify-between gap-4">
            {/* Left Section - Catalog Info */}
            {renderLeftSection()}

            {/* Center Section - Page Navigation */}
            <div className="hidden md:flex items-center gap-1">
              {renderNavigation()}
            </div>

            {/* Right Section - Actions */}
            <div className="flex items-center gap-2">
              {onSearch && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onSearch}
                  iconName="Search"
                  iconSize={16}
                  title={t('catalogViewer.toolbar.search')}
                />
              )}
              {onMenu && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onMenu}
                  iconName="Menu"
                  iconSize={16}
                  title={t('catalogViewer.toolbar.menu')}
                />
              )}
              <div className="relative">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsShareMenuOpen(!isShareMenuOpen)}
                  iconName="Share2"
                  iconSize={16}
                  title={t('catalogViewer.toolbar.share')}
                />
                {isShareMenuOpen && renderShareMenu(false)}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onDownload}
                iconName="Download"
                iconSize={16}
                title={t('catalogViewer.toolbar.download')}
              />
              <Button
                variant="ghost"
                size="sm"
                onClick={onEmbed}
                iconName="Code"
                iconSize={16}
                title={t('catalogViewer.toolbar.embed')}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewerToolbar;
